import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import { dispatchBadge, formatPct, meterTone, rowBadge } from '../presentation';
import type { TrainingRow } from '../types';

export const COLUMNS = ['Documento', 'Asignados', 'Avance', 'Estado'] as const;

interface Props {
  rows: TrainingRow[];
  selected: string | null;
  onSelect: (entryId: string) => void;
}

/** Capacitaciones por documento (mockup 06): asignados, avance de lectura y estado,
 *  con la insignia del último disparo. Elegir un documento abre su detalle. */
export function TrainingTable({ rows, selected, onSelect }: Props): ReactElement {
  return (
    <div className="t-scroll">
      <table className="t">
        <caption className="sr-only">Avance de lectura por documento de la empresa activa</caption>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c} scope="col">
                {c === 'Estado' ? <span className="sr-only">{c}</span> : c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const badge = rowBadge(r);
            const dispatch = dispatchBadge(r.dispatch);
            const isSel = r.entryId === selected;
            return (
              <tr
                key={r.entryId}
                className={cn('pick', isSel && 'sel')}
                onClick={() => onSelect(r.entryId)}
              >
                <td>
                  <button
                    type="button"
                    className="rowpick"
                    aria-pressed={isSel}
                    aria-label={`Ver el detalle de ${r.code}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(r.entryId);
                    }}
                  >
                    <span className="code">{r.code}</span>
                    {r.title && <div className="sub">{r.title}</div>}
                  </button>
                </td>
                <td className="dim-mono">{r.assigned.toLocaleString('es-CR')}</td>
                <td>
                  <div className="rowb">
                    <div
                      className={cn('meter', meterTone(r))}
                      role="progressbar"
                      aria-label={`Avance de lectura de ${r.code}`}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(r.progressPct ?? 0)}
                    >
                      <i style={{ width: `${r.progressPct ?? 0}%` }} />
                    </div>
                    <span className="dim-mono">{formatPct(r.progressPct)}</span>
                  </div>
                </td>
                <td>
                  <div className="badges">
                    <span className={cn('badge', badge.cls)}>{badge.label}</span>
                    {dispatch && (
                      <span className={cn('badge', dispatch.cls)}>{dispatch.label}</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
